import type { ToolCallMessagePartProps } from "@assistant-ui/react";
import { ArrowUpRight, Check, ChevronDown, X } from "lucide-react";
import { useContext, useState } from "react";
import { CopilotActionContext } from "@/features/copilot/components/copilot-action-context";
import { CopilotToolChip } from "@/features/copilot/components/copilot-tool-chip";
import { hasExecutingSiblingAction } from "@/features/copilot/lib/copilot-action-blocking";
import {
  copilotActionConsequence,
  copilotActionDecisionEnabled,
  copilotActionFields,
  copilotActionNotice,
  copilotActionStatusLabel,
  copilotActionTitleId,
} from "@/features/copilot/lib/copilot-action-display";
import { copilotActionRecordLink } from "@/features/copilot/lib/copilot-action-record-link";
import type { CopilotActionView } from "@/features/copilot/schemas/copilot.schema";
import { Button } from "@/shared/components/ui/button";
import { cn } from "@/shared/lib/cn";

/// Status reads at a glance: amber while it needs or awaits something, green once applied, muted
/// when nothing happened. The text beside it carries the meaning; the dot never stands alone.
const STATUS_DOT: Record<CopilotActionView["status"], string> = {
  pending: "bg-warning",
  running: "bg-warning",
  confirmed: "bg-success",
  declined: "bg-text-tertiary",
  expired: "bg-text-tertiary",
  failed: "bg-destructive",
  unknown: "bg-warning",
};

export function CopilotActionCard(props: ToolCallMessagePartProps) {
  const context = useContext(CopilotActionContext);
  const action = context?.actions.get(props.toolCallId);
  const [decisionError, setDecisionError] = useState(false);

  if (!action) return <CopilotToolChip {...props} />;

  const siblingExecuting = hasExecutingSiblingAction(
    context?.actions.values() ?? [],
    action.toolCallId,
  );
  const canDecide = copilotActionDecisionEnabled({
    approvalRequested: props.approval !== undefined && props.approval.approved === undefined,
    approvalSending: context?.approvalSending ?? false,
    serverWaiting: context?.canRespond === true,
    runtimeIsIdle: context?.runtimeIsIdle === true,
    siblingExecuting,
    status: action.status,
  });

  function decide(approved: boolean) {
    if (!context || !canDecide || !props.approval) return;
    setDecisionError(false);
    void context
      .respondToAction(() => props.respondToApproval({ approved }), context.runtimeIsIdle)
      .catch(() => setDecisionError(true));
  }

  const decisionStyling = action.risk === "decision";
  const status = copilotActionStatusLabel(action.status);
  // Model-facing text (the raw result, the decline reason, the capability name) stays off the card.
  const fields = copilotActionFields(action);
  const argValues = new Set(Object.values(action.args).flat().map(String));
  const notice = copilotActionNotice(action);
  const consequence = copilotActionConsequence(action);
  const recordLink = context ? copilotActionRecordLink(action, context.workspaceSlug) : null;
  const otherTargets = action.summary.targets.filter((target) => !argValues.has(target.id));

  return (
    <section
      aria-label={`Hand confirmation: ${action.summary.title}`}
      data-capability={action.capability}
      className={cn(
        "my-3 overflow-hidden rounded-xl border bg-card text-sm shadow-sm",
        decisionStyling ? "border-amber-500/40" : "border-border",
      )}
    >
      <div className="space-y-3 p-4">
        <div className="min-w-0 space-y-1">
          <h3
            tabIndex={-1}
            id={copilotActionTitleId(action.toolCallId)}
            className="font-medium leading-5"
          >
            {action.summary.title}
          </h3>
          <p
            className={cn(
              "text-xs",
              decisionStyling ? "text-amber-700 dark:text-amber-300" : "text-text-secondary",
            )}
          >
            {decisionStyling ? "Decision · " : null}
            <span className="inline-flex items-center gap-1.5">
              <span
                aria-hidden="true"
                className={cn(
                  "size-1.5 rounded-full",
                  STATUS_DOT[action.status],
                  action.status === "running" && "motion-safe:animate-pulse",
                )}
              />
              {status}
            </span>
          </p>
        </div>

        {action.summary.facts ? (
          <ul className="list-disc space-y-0.5 pl-5 text-sm leading-5 marker:text-text-tertiary">
            {action.summary.facts.map((fact, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: reason: facts are server-written sentences that may repeat within one action, so the text alone is not a unique key
              <li key={`${index}-${fact}`} className="break-words">
                {fact}
              </li>
            ))}
          </ul>
        ) : null}

        {fields.primary.length > 0 ? (
          <dl className="space-y-2 rounded-md bg-muted/40 px-3 py-2">
            {fields.primary.map((field) => (
              <div key={field.label}>
                <dt className="text-xs text-text-secondary">{field.label}</dt>
                <dd className="whitespace-pre-wrap break-words">{field.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}

        {otherTargets.length > 0 ? (
          <p className="break-words text-xs text-text-secondary">
            Affects {otherTargets.map((target) => target.label).join(", ")}
          </p>
        ) : null}

        {notice ? <p className="text-sm leading-5">{notice}</p> : null}
        {consequence ? <p className="text-sm leading-5">{consequence}</p> : null}

        {fields.technical.length > 0 ? (
          <details className="group text-xs text-text-tertiary">
            <summary className="flex cursor-pointer list-none items-center gap-1 rounded focus-visible:outline-2 focus-visible:outline-ring">
              Technical details
              <ChevronDown
                aria-hidden="true"
                className="size-3.5 transition-transform group-open:rotate-180"
              />
            </summary>
            <dl className="mt-1 space-y-0.5">
              {fields.technical.map((field) => (
                <div key={field.label} className="flex gap-2">
                  <dt>{field.label}</dt>
                  <dd className="min-w-0 break-all">{field.value}</dd>
                </div>
              ))}
            </dl>
          </details>
        ) : null}

        {recordLink ? (
          <a
            className="inline-flex items-center gap-0.5 text-xs text-primary hover:underline focus-visible:outline-2 focus-visible:outline-ring"
            href={recordLink.href}
          >
            {recordLink.label}
            <ArrowUpRight aria-hidden="true" className="size-3" />
          </a>
        ) : null}
        {action.error && action.status !== "declined" ? (
          <p role="status" aria-live="polite" className="rounded-md bg-muted/50 px-3 py-2 text-sm">
            {action.error.message}
          </p>
        ) : null}
        {action.status === "unknown" ? (
          <p className="text-xs text-text-secondary">
            {action.reconciling
              ? "This request may still finish in the background. Check the record before trying anything again. Hand will not repeat this change."
              : "Check the record before trying anything again. Hand will not repeat this change."}
          </p>
        ) : null}
        {decisionError ? (
          <p role="alert" className="text-xs text-destructive">
            Your decision was not sent. Refresh the action state before trying again.
          </p>
        ) : null}

        {action.status === "pending" && siblingExecuting ? (
          <p className="text-xs text-text-secondary">
            Another change is still being applied. You can decide once it finishes.
          </p>
        ) : null}
        {action.status === "pending" ? (
          <div className="flex flex-wrap gap-2 pointer-coarse:flex-col">
            <Button
              type="button"
              size="sm"
              className="pointer-coarse:h-11 pointer-coarse:w-full"
              disabled={!canDecide}
              onClick={() => decide(true)}
            >
              <Check aria-hidden="true" className="size-4" />
              Confirm
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="pointer-coarse:h-11 pointer-coarse:w-full"
              disabled={!canDecide}
              onClick={() => decide(false)}
            >
              <X aria-hidden="true" className="size-4" />
              Decline
            </Button>
          </div>
        ) : null}
      </div>
    </section>
  );
}
